import React from "react";
import { Control, FieldValues } from "react-hook-form";
import {
  Input,
  Label,
  DatePicker,
  FormErrorMessage,
} from "@src/common/components";
import { Controller } from "react-hook-form";
import "react-datepicker/dist/react-datepicker.css";
import { get } from "lodash";
import { getCourierTodayForDatePicker } from "@src/common/utils/timezoneConverters";

interface FormInputProps {
  name: string;
  label: string;
  control: Control<FieldValues>;
  errors: any;
  isDatePicker?: boolean;
  type?: string;
  step?: number | "any";
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
}

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  control,
  errors,
  isDatePicker = false,
  type = "text",
  step,
  placeholder = "",
  required = false,
  min = undefined,
  max = undefined,
}) => {
  const rules = {
    ...(required ? { required: `${label} is required` } : {}),
    ...(min != null ? { min: { value: min, message: `${label} must be at least ${min}` } } : {}),
    ...(max != null ? { max: { value: max, message: `${label} must be at most ${max}` } } : {}),
  };

  return (
    <div className="flex flex-col justify-between">
      <Label
        htmlFor={name}
        className="flex items-center text-xs font-medium text-gray-700 dark:text-gray-300"
      >
        <span className="truncate">{label}</span>
        {required ? (
          <span className="text-red-500 shrink-0"> *</span>
        ) : (
          <span className="text-slate-400 dark:text-gray-500 text-xs shrink-0"> (Optional)</span>
        )}
      </Label>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) =>
          isDatePicker ? (
            <DatePicker
              size="mini"
              value={field.value}
              formatString={"MMM do, yyyy"}
              onChange={({ date }) => field.onChange(date)}
              minDate={getCourierTodayForDatePicker()}
              error={
                errors?.shipments && errors?.shipments[0]?.shipmentDate?.message
              }
            />
          ) : (
            <Input
              size="mini"
              value={field.value ?? ""}
              onChange={(event) => {
                if (type !== "number") {
                  field.onChange(event.currentTarget.value);
                } else {
                  const parse = step === 1 ? parseInt : parseFloat;
                  const value = parse(event.currentTarget.value);
                  Number.isNaN(value)
                    ? field.onChange(null)
                    : field.onChange(value);
                }
              }}
              onBlur={field.onBlur}
              placeholder={placeholder}
              type={type}
              step={step}
              error={!!get(errors, name)}
              min={min}
              max={max}
            />
          )
        }
      />
      <span className="text-red-500 text-xs">
        <FormErrorMessage errors={errors} name={name} />
      </span>
    </div>
  );
};

export default FormInput;
